/*
  Set of generic utility functions that can be used across the app
*/

import * as convert from 'convert-units';
import { nanoid } from 'nanoid';

import { type WeightUnit } from '~/constants/weight';
import { type BlackbeardWeightUnit } from '~/pages/Orders/Blackbeard/LabelPurchase/modules/types';

/**
 * This function correctly type guards and type narrows for not null or undefined
 * so the TS compiler understands a value exists after the call to this function.
 * @param val any value type
 * @returns true if the value is not undefined or null, false otherwise
 */
export const isNotNil = <T>(val: T): val is Exclude<T, null | undefined> =>
  val !== null && val !== undefined;

/**
 * Curried
 * rounds the number `num` to `precision` decimal points
 * @param precision
 * @param num floating point number
 * @returns floating point  number
 */
export const roundFloat = (precision: number, num: number): number => {
  const expo = Math.pow(10, precision);
  return Math.round((num + Number.EPSILON) * expo) / expo;
};

/**
 * Curried
 * function that converts the number `val` from the `from` units
 * to the `to` units
 */
export const convertUnits = (
  from: BlackbeardWeightUnit | WeightUnit,
  to: BlackbeardWeightUnit | WeightUnit,
  val: number,
): number => convert(val).from(from).to(to);

interface HasToString {
  toString: () => string;
}

const hasToString = (obj: unknown): obj is HasToString =>
  (obj as HasToString).toString !== undefined;

export const unknownToString = (input: unknown): string => {
  if (isNotNil(input)) {
    if (hasToString(input)) {
      let value = input.toString();

      if (value === '[object Object]') value = JSON.stringify(input);

      return value;
    }
  }

  return '';
};

export const formatTestIdName = (name: string): string =>
  name.toLowerCase().trim().replaceAll(' ', '-');

/**
 * remove leading and trailing spaces from each number and string value
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const trimObjectValues = (obj: Record<string, any>): Record<string, any> =>
  Object.entries(obj).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: ['number', 'string'].includes(typeof value) ? value.trim() : value,
    }),
    obj,
  );

/**
 * Copy the given value to the clipboard
 * @param value string value to copy to the clipboard
 */
export const copyToClipboard = (value: Maybe<string>) => {
  if (!value) return;

  if ('clipboard' in navigator) navigator.clipboard.writeText(value);
  else legacyCopyToClipboard(value);
};

const legacyCopyToClipboard = (trackingNumber: string) => {
  const textArea = document.createElement('textarea');
  textArea.textContent = trackingNumber;
  document.body.appendChild(textArea);
  const selection = document.getSelection();
  const range = document.createRange();
  range.selectNode(textArea);
  selection?.removeAllRanges();
  selection?.addRange(range);
  document.execCommand('copy');
  selection?.removeAllRanges();
  document.body.removeChild(textArea);
};

/**
 * Generates a random ID string for input elements
 * @returns unique string id
 */
export const generateRandomInputId = () => {
  const id = nanoid();

  return `input-${id}`;
};
